import { useState, useCallback } from 'react';
import ScenarioCard from './ScenarioCard';
import ScenarioForm from './ScenarioForm';
import ScenarioBreakEven from './ScenarioBreakEven';
import ImpactPanel from './ImpactPanel';
import SectionLabel from '../common/SectionLabel';
import { calcScenarioMonthly } from '../../utils/calculations';
import { useFinancialData } from '../../context/FinancialDataContext';
import { usePipeline } from '../../context/PipelineContext';
import { formatCurrency } from '../../utils/formatting';

// Get the display monthly cost for a scenario card
function getScenarioMonthlyCost(scenario) {
  switch (scenario.type) {
    case 'new-hire-support':
      return -(scenario.monthlySalary * (1 + scenario.payrollTaxRate));
    case 'new-hire-revenue': {
      const cost = scenario.monthlySalary * (1 + scenario.payrollTaxRate);
      return scenario.monthlyRevenue - cost;
    }
    case 'one-time-expense':
      return -scenario.amount;
    case 'recurring-expense':
      return scenario.monthlyAmount;
    case 'revenue-change':
      return scenario.monthlyAmount;
    default:
      return 0;
  }
}

// Get annual impact for card display
function getScenarioAnnualImpact(scenario) {
  const monthly = calcScenarioMonthly(scenario);
  return monthly.reduce((sum, m) => sum + m.net, 0);
}

export default function ScenarioPlanner({
  scenarios,
  setScenarios,
  profitFloorPct,
  setProfitFloorPct,
  monthlyImpact,
}) {
  const [showForm, setShowForm] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const { annualSummary, global2026Monthly } = useFinancialData();
  const { pipelineKPIs, prospects } = usePipeline();

  // Compute average deal value from pipeline prospects for cross-reference
  const avgDealValue = (() => {
    if (!prospects || prospects.length === 0) return 0;
    const withValue = prospects.filter(p => {
      const ev = p.estimatedValue;
      if (!ev) return false;
      const total = (ev.oneTime || 0) + ((ev.monthlyRecurring || 0) * 12) + ((ev.aum || 0) * (ev.aumFeeRate || 0));
      return total > 0;
    });
    if (withValue.length === 0) return 0;
    const sum = withValue.reduce((s, p) => {
      const ev = p.estimatedValue;
      return s + (ev.oneTime || 0) + ((ev.monthlyRecurring || 0) * 12) + ((ev.aum || 0) * (ev.aumFeeRate || 0));
    }, 0);
    return sum / withValue.length;
  })();

  // Get next month (1-indexed) for template defaults
  const nextMonth = (() => {
    const now = new Date();
    const m = now.getMonth() + 2; // getMonth is 0-indexed, +1 for 1-indexed, +1 for next month
    return m > 12 ? 1 : m;
  })();

  // Quick-add template definitions
  const TEMPLATES = [
    {
      label: 'Hire Support Staff',
      icon: '\u{1F464}',
      build: () => ({
        id: `scenario-${Date.now()}`,
        type: 'new-hire-support',
        title: 'Support Staff',
        monthlySalary: 4500,
        payrollTaxRate: 0.10,
        startMonth: nextMonth,
        active: true,
      }),
    },
    {
      label: 'Hire Revenue Producer',
      icon: '\u{1F4BC}',
      build: () => ({
        id: `scenario-${Date.now()}`,
        type: 'new-hire-revenue',
        title: 'Revenue Producer',
        monthlySalary: 5000,
        payrollTaxRate: 0.10,
        monthlyRevenue: 8000,
        startMonth: nextMonth,
        revenueStartMonth: Math.min(nextMonth + 2, 12),
        active: true,
      }),
    },
    {
      label: 'Add Software Tool',
      icon: '\u{1F4BB}',
      build: () => ({
        id: `scenario-${Date.now()}`,
        type: 'recurring-expense',
        title: 'New Software',
        monthlyAmount: -500,
        startMonth: nextMonth,
        active: true,
      }),
    },
    {
      label: 'Increase Marketing',
      icon: '\u{1F4E3}',
      build: () => ({
        id: `scenario-${Date.now()}`,
        type: 'recurring-expense',
        title: 'Marketing Budget Increase',
        monthlyAmount: -2000,
        startMonth: nextMonth,
        active: true,
      }),
    },
    {
      label: 'New Revenue Stream',
      icon: '\u{1F4C8}',
      build: () => ({
        id: `scenario-${Date.now()}`,
        type: 'revenue-change',
        title: 'New Revenue',
        monthlyAmount: 3000,
        startMonth: nextMonth,
        category: 'Subscription Fees',
        active: true,
      }),
    },
  ];

  // Base financials from 2026 projected data (now live from context)
  const current2026 = annualSummary.find((d) => d.year === 2026);
  const baseAnnualRevenue = current2026 ? current2026.revenue : 0;

  // Compute actual monthly NOI from P&L data (not flat average)
  const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const baseMonthlyNOIArray = MONTH_KEYS.map(mk => {
    const md = global2026Monthly?.months?.[mk];
    if (!md) return 0;
    const income = Object.values(md.income || {}).reduce((s, v) => s + (v || 0), 0);
    const cogs = Object.values(md.cogs || {}).reduce((s, v) => s + (v || 0), 0);
    const expenses = Object.values(md.expenses || {}).reduce((s, v) => s + (v || 0), 0);
    return income - cogs - expenses;
  });
  const baseMonthlyIncomeArray = MONTH_KEYS.map(mk => {
    const md = global2026Monthly?.months?.[mk];
    if (!md) return 0;
    return Object.values(md.income || {}).reduce((s, v) => s + (v || 0), 0);
  });

  const handleToggle = useCallback((id) => {
    setScenarios((prev) =>
      prev.map((s) => (s.id === id ? { ...s, active: !s.active } : s))
    );
  }, [setScenarios]);

  const handleDelete = useCallback((id) => {
    setScenarios((prev) => prev.filter((s) => s.id !== id));
  }, [setScenarios]);

  const handleAdd = useCallback((scenario) => {
    if (editingScenario) {
      // Update existing scenario
      setScenarios((prev) => prev.map(s => s.id === editingScenario.id ? { ...scenario, id: editingScenario.id, active: editingScenario.active } : s));
      setEditingScenario(null);
    } else {
      setScenarios((prev) => [...prev, scenario]);
    }
    setShowForm(false);
  }, [setScenarios, editingScenario]);

  // Quick-add a template scenario
  const handleQuickAdd = useCallback((template) => {
    const scenario = template.build();
    setScenarios((prev) => [...prev, scenario]);
  }, [setScenarios]);

  // Pipeline cross-reference note for revenue scenarios
  function PipelineNote({ scenario }) {
    // Only show for revenue-generating scenarios
    if (scenario.type !== 'new-hire-revenue' && scenario.type !== 'revenue-change') return null;

    const scenarioMonthlyRev = scenario.type === 'new-hire-revenue'
      ? (scenario.monthlyRevenue || 0)
      : (scenario.monthlyAmount || 0);

    if (scenarioMonthlyRev <= 0) return null;

    const weightedMonthly = (pipelineKPIs?.weightedValue || 0) / 12;
    // If scenario revenue exceeds pipeline's monthly weighted value, flag as ambitious
    const isAmbitious = scenarioMonthlyRev > weightedMonthly && weightedMonthly > 0;
    const noteColor = isAmbitious ? 'bg-[#FFF8E1] border-[#FFD54F]' : 'bg-[#E8F5E9] border-[#81C784]';
    const textColor = isAmbitious ? 'text-[#F57F17]' : 'text-[#006D78]';

    return (
      <div className={`${noteColor} border rounded-[4px] px-3 py-2 mt-1 ml-4 text-[11px] ${textColor}`}>
        <span>
          This scenario assumes {formatCurrency(scenarioMonthlyRev)}/mo new revenue.
          {pipelineKPIs?.weightedValue > 0 && (
            <> Pipeline weighted value: {formatCurrency(pipelineKPIs.weightedValue)}. </>
          )}
          {avgDealValue > 0 && (
            <>Avg deal: {formatCurrency(avgDealValue)}. </>
          )}
        </span>
        {isAmbitious && (
          <span className="font-bold"> Ambitious relative to current pipeline.</span>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[380px_1fr] gap-6">
      {/* LEFT PANEL: Scenario List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <SectionLabel>Scenarios</SectionLabel>
          {!showForm && (
            <button
              onClick={() => { setEditingScenario(null); setShowForm(true); }}
              className="px-3 py-1.5 text-[12px] font-bold text-white bg-bullogic-blue rounded-[4px] hover:bg-bullogic-blue/90"
            >
              + Add Scenario
            </button>
          )}
        </div>

        {/* Quick Add Templates */}
        {!showForm && (
          <div className="mb-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#888] mb-2">Quick Add</div>
            <div className="flex flex-wrap gap-1.5">
              {TEMPLATES.map((tmpl) => (
                <button
                  key={tmpl.label}
                  onClick={() => handleQuickAdd(tmpl)}
                  className="px-2.5 py-1.5 text-[11px] bg-gray-100 hover:bg-gray-200 text-navy rounded-[4px] transition-colors"
                  title={tmpl.label}
                >
                  {tmpl.icon} {tmpl.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <div className="mb-4">
            <ScenarioForm
              onAdd={handleAdd}
              onCancel={() => { setShowForm(false); setEditingScenario(null); }}
              editingScenario={editingScenario}
            />
          </div>
        )}

        {/* Scenario cards */}
        <div className="space-y-3">
          {scenarios.length === 0 && !showForm && (
            <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-6 text-center">
              <p className="text-[13px] text-[#888]">No scenarios yet. Click "Add Scenario" or use Quick Add to model a what-if.</p>
            </div>
          )}
          {scenarios.map((scenario) => (
            <div key={scenario.id}>
              <ScenarioCard
                scenario={scenario}
                monthlyCost={getScenarioMonthlyCost(scenario)}
                annualImpact={getScenarioAnnualImpact(scenario)}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onEdit={() => { setEditingScenario(scenario); setShowForm(true); }}
              />
              {/* Break-even analysis for revenue hires */}
              {scenario.active && <ScenarioBreakEven scenario={scenario} />}
              {/* Pipeline cross-reference for revenue scenarios */}
              {scenario.active && <PipelineNote scenario={scenario} />}
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT PANEL: Impact Analysis */}
      <div>
        <div className="mb-4">
          <SectionLabel>Cumulative Impact</SectionLabel>
        </div>
        <ImpactPanel
          monthlyImpact={monthlyImpact}
          profitFloorPct={profitFloorPct}
          onProfitFloorPctChange={setProfitFloorPct}
          baseMonthlyNOIArray={baseMonthlyNOIArray}
          baseMonthlyIncomeArray={baseMonthlyIncomeArray}
          baseAnnualRevenue={baseAnnualRevenue}
          scenarios={scenarios}
        />
      </div>
    </div>
  );
}
